import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Modal from '../../../../components/ModalComponents/Modal';
import { toast } from 'react-toastify';
import { mapToOption, formatDateCST, formatTimeCST } from '../../../../utlis';
import useAddressValidation from '../../../../hooks/useAddressValidation';
import AddressValidationDialog from '../../../../components/AddressValidationDialog';

// Format phone number as (999) 999 9999
const formatPhoneNumber = (value) => {
  if (!value) return '';
  // Remove all non-numeric characters
  const digits = value.replace(/\D/g, '');
  // Format based on length
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
};

const SalesContactsModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  defaultValues,
}) => {
  const referenceData = useSelector((state) => state.reference);
  const jobs = useSelector((state) => state.reference.jobtitles?.job);
  const {
    validateAndProceed, isValidating, dialogState,
    acceptSuggestion, keepOriginal, saveAnyway, cancelDialog,
  } = useAddressValidation({
    fieldMap: { street: 'address1', street2: 'address2', zip: 'zipCode' },
    countryField: 'country',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      vendorStatus: 'Active',
    },
  });

  const vendorStatus = watch('vendorStatus');
  const originalContactRef = useRef(null);
  const [isEdited, setIsEdited] = useState(false);

  // Reset form when modal opens or defaultValues change
  useEffect(() => {
    if (isOpen && defaultValues) {
      // Format phone numbers before resetting
      const formattedDefaults = {
        ...defaultValues,
        mobileNum1: formatPhoneNumber(defaultValues.mobileNum1),
        mobileNum2: formatPhoneNumber(defaultValues.mobileNum2),
        fax: formatPhoneNumber(defaultValues.fax),
      };
      reset(formattedDefaults);
      originalContactRef.current = formattedDefaults;
      setIsEdited(false);
    }
  }, [isOpen, defaultValues, reset]);

  // Handle phone input change with formatting
  const handlePhoneChange = (e, fieldName) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue(fieldName, formatted);
  };

  // Compare form data with original data to enable Update Details button
  useEffect(() => {
    if (!isOpen || !originalContactRef.current) {
      setIsEdited(false);
      return;
    }
    const subscription = watch((formData) => {
      const original = originalContactRef.current;
      const isDifferent = Object.keys(original).some((key) => {
        const origVal = original[key] ?? '';
        const formVal = formData[key] ?? '';
        return origVal !== formVal;
      });
      setIsEdited(isDifferent);
    });
    return () => subscription.unsubscribe();
  }, [isOpen, watch]);

  const handleClose = () => {
    reset({
      vendorStatus: 'Active',
    });
    onClose();
  };

  const handleFormSubmit = (data) => {
    console.log('Form submit data:', { address1: data.address1, city: data.city, state: data.state, zipCode: data.zipCode, country: data.country });
    validateAndProceed(data, (validatedData) => {
      // Update form fields with validated address values (city/state/country from USPS)
      if (validatedData.city !== data.city) setValue('city', validatedData.city);
      if (validatedData.state !== data.state) setValue('state', validatedData.state);
      if (validatedData.zipCode !== data.zipCode) setValue('zipCode', validatedData.zipCode);
      if (validatedData.address1 !== data.address1) setValue('address1', validatedData.address1);
      if (validatedData.address2 !== data.address2) setValue('address2', validatedData.address2);

      // Auto-set country to US since USPS validated the address
      if (!validatedData.country?.trim()) {
        validatedData.country = 'US';
        setValue('country', 'US');
      }

      // Block save if city or state is still empty after validation
      if (!validatedData.city?.trim() || !validatedData.state?.trim()) {
        toast.error('City and State are required. Please validate your address or enter them manually.');
        return;
      }
      onSubmit(validatedData);
    });
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="relative">
        <div className="flex items-center mb-6">
          {isEditMode ? (
            <h2 className="text-2xl font-bold text-black pt-2">
              Edit Contact Details
              <span style={{ color: 'rgb(178, 120, 33)', fontWeight: 'bold' }}>
                {' '}
                - {defaultValues?.firstName}
                {defaultValues?.lastName ? ` ${defaultValues.lastName}` : ''}
              </span>
            </h2>
          ) : (
            <h2 className="text-2xl font-bold text-black pt-2">Add Contact</h2>
          )}
        </div>
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none z-10"
          aria-label="Close"
          type="button"
        >
          &times;
        </button>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-6 text-text-hint">
          {/* Row 1: First Name, Middle Name, Last Name */}
          <div className="grid grid-cols-3 gap-5 mt-10">
            <div className="flex flex-col w-72">
              <label htmlFor="firstName" className="text-text-hint mb-1">
                First Name<span className="text-danger-500">*</span>
              </label>
              <input
                {...register('firstName', {
                  required: 'First Name is required',
                  validate: (value) => value?.trim() !== '' || 'First Name cannot be blank',
                  minLength: { value: 2, message: 'First Name must be at least 2 characters' },
                })}
                type="text"
                placeholder="First Name"
                id="firstName"
                className={`outline-none border-2 rounded-lg text-black pencialicon ${errors.firstName ? 'border-danger-500' : 'border-secondary-500'}`}
              />
              {errors.firstName && (
                <span className="text-danger-500 text-sm">{errors.firstName.message}</span>
              )}
            </div>
            <div className="flex flex-col w-72">
              <label htmlFor="middleName" className="text-text-hint mb-1">
                Middle Name
              </label>
              <input
                {...register('middleName')}
                type="text"
                placeholder="Middle Name"
                id="middleName"
                className="outline-none border-2 border-secondary-500 rounded-lg text-black pencialicon"
              />
            </div>
            <div className="flex flex-col w-72">
              <label htmlFor="lastName" className="text-text-hint mb-1">
                Last Name<span className="text-danger-500">*</span>
              </label>
              <input
                {...register('lastName', {
                  required: 'Last Name is required',
                  validate: (value) => value?.trim() !== '' || 'Last Name cannot be blank',
                  minLength: { value: 2, message: 'Last Name must be at least 2 characters' },
                })}
                type="text"
                placeholder="Last Name"
                id="lastName"
                className={`outline-none border-2 rounded-lg text-black pencialicon ${errors.lastName ? 'border-danger-500' : 'border-secondary-500'}`}
              />
              {errors.lastName && (
                <span className="text-danger-500 text-sm">{errors.lastName.message}</span>
              )}
            </div>
          </div>

          {/* Row 2: Preferred Name, Email, Job Title */}
          <div className="grid grid-cols-3 gap-5">
            <div className="flex flex-col w-72">
              <label htmlFor="preferredName" className="text-text-hint mb-1">
                Preferred Name<span className="text-danger-500">*</span>
              </label>
              <input
                {...register('preferredName', {
                  required: 'Preferred Name is required',
                  validate: (value) => value?.trim() !== '' || 'Preferred Name cannot be blank',
                })}
                type="text"
                placeholder="Preferred Name"
                id="preferredName"
                className={`outline-none border-2 rounded-lg text-black pencialicon ${errors.preferredName ? 'border-danger-500' : 'border-secondary-500'}`}
              />
              {errors.preferredName && (
                <span className="text-danger-500 text-sm">{errors.preferredName.message}</span>
              )}
            </div>
            <div className="flex flex-col w-72">
              <label htmlFor="email" className="text-text-hint mb-1">
                Email<span className="text-danger-500">*</span>
              </label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: 'Please enter a valid email address',
                  },
                })}
                type="email"
                id="email"
                placeholder="youremail@example.com"
                className={`outline-none border-2 rounded-lg text-black pencialicon ${errors.email ? 'border-danger-500' : 'border-secondary-500'}`}
              />
              {errors.email && <span className="text-danger-500 text-sm">{errors.email.message}</span>}
            </div>
            <div className="flex flex-col w-72">
              <label htmlFor="jobTitle" className="text-text-hint mb-1">
                Job Title<span className="text-danger-500">*</span>
              </label>
              <select
                {...register('jobTitle', {
                  required: 'Please select a Job Title',
                  validate: (value) => value !== '' || 'Please select a Job Title',
                })}
                id="jobTitle"
                className={`outline-none border-2 rounded-lg text-black dropdown ${errors.jobTitle ? 'border-danger-500' : 'border-secondary-500'}`}
              >
                <option value="">-- Select Job Title --</option>
                {mapToOption(jobs || [], 'title').map((option) => (
                  <option className="" key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
              {errors.jobTitle && (
                <span className="text-danger-500 text-sm">{errors.jobTitle.message}</span>
              )}
            </div>
          </div>

          {/* Row 3: Mobile Number, Alt Phone Number, Fax */}
          <div className="grid grid-cols-3 gap-5">
            <div className="flex flex-col w-72">
              <label htmlFor="mobileNum1" className="text-text-hint mb-1">
                Mobile Number<span className="text-danger-500">*</span>
              </label>
              <input
                {...register('mobileNum1', {
                  required: 'Mobile Number is required',
                  pattern: {
                    value: /^\(\d{3}\)\s\d{3}\s\d{4}$/,
                    message: 'Please enter a valid Mobile Number (999) 999 9999',
                  },
                })}
                type="text"
                placeholder="(999) 999 9999"
                id="mobileNum1"
                maxLength={14}
                onChange={(e) => handlePhoneChange(e, 'mobileNum1')}
                className={`outline-none border-2 rounded-lg text-black pencialicon ${errors.mobileNum1 ? 'border-danger-500' : 'border-secondary-500'}`}
              />
              {errors.mobileNum1 && (
                <span className="text-danger-500 text-sm">{errors.mobileNum1.message}</span>
              )}
            </div>
            <div className="flex flex-col w-72">
              <label htmlFor="mobileNum2" className="text-text-hint mb-1">
                Alt Phone Number
              </label>
              <input
                {...register('mobileNum2', {
                  pattern: {
                    value: /^$|^\(\d{3}\)\s\d{3}\s\d{4}$/,
                    message: 'Please enter a valid Alt Phone Number (999) 999 9999',
                  },
                })}
                type="text"
                placeholder="(999) 999 9999"
                id="mobileNum2"
                maxLength={14}
                onChange={(e) => handlePhoneChange(e, 'mobileNum2')}
                className={`outline-none border-2 rounded-lg text-black pencialicon ${errors.mobileNum2 ? 'border-danger-500' : 'border-secondary-500'}`}
              />
              {errors.mobileNum2 && (
                <span className="text-danger-500 text-sm">{errors.mobileNum2.message}</span>
              )}
            </div>
            <div className="flex flex-col w-72">
              <label htmlFor="fax" className="text-text-hint mb-1">
                Fax
              </label>
              <input
                {...register('fax', {
                  pattern: {
                    value: /^$|^\(\d{3}\)\s\d{3}\s\d{4}$/,
                    message: 'Please enter a valid Fax number (999) 999 9999',
                  },
                })}
                type="text"
                placeholder="(999) 999 9999"
                id="fax"
                maxLength={14}
                onChange={(e) => handlePhoneChange(e, 'fax')}
                className={`outline-none border-2 rounded-lg text-black pencialicon ${errors.fax ? 'border-danger-500' : 'border-secondary-500'}`}
              />
              {errors.fax && (
                <span className="text-danger-500 text-sm">{errors.fax.message}</span>
              )}
            </div>
          </div>

          {/* Row 4: Contact Type */}
          <div className="grid grid-cols-3 gap-5">
            <div className="flex flex-col w-72">
              <label htmlFor="contactType" className="text-text-hint mb-1">
                Contact Type<span className="text-danger-500">*</span>
              </label>
              <select
                {...register('contactType', {
                  required: 'Please select a Contact Type',
                  validate: (value) => value !== '' || 'Please select a Contact Type',
                })}
                id="contactType"
                className={`outline-none border-2 rounded-lg text-black dropdown ${errors.contactType ? 'border-danger-500' : 'border-secondary-500'}`}
              >
                <option value="">
                  -- Select Contact Type --
                </option>
                {mapToOption(referenceData.contacttypes?.contact, 'type').map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
              {errors.contactType && (
                <span className="text-danger-500 text-sm">{errors.contactType.message}</span>
              )}
            </div>
          </div>

          {/* Row 5: Address Line 1, Address Line 2 */}
          <div className="grid grid-cols-3 gap-5">
            <div className="flex flex-col w-72">
              <label htmlFor="address1" className="text-text-hint mb-1">
                Address Line 1<span className="text-danger-500">*</span>
              </label>
              <input
                {...register('address1', {
                  required: 'Address Line 1 is required',
                  validate: (value) => value?.trim() !== '' || 'Address Line 1 cannot be blank',
                })}
                type="text"
                placeholder="Address Line 1"
                id="address1"
                className={`outline-none border-2 rounded-lg text-black pencialicon ${errors.address1 ? 'border-danger-500' : 'border-secondary-500'}`}
              />
              {errors.address1 && (
                <span className="text-danger-500 text-sm">{errors.address1.message}</span>
              )}
            </div>
            <div className="flex flex-col w-72">
              <label htmlFor="address2" className="text-text-hint mb-1">
                Address Line 2
              </label>
              <input
                {...register('address2')}
                type="text"
                placeholder="Address Line 2"
                id="address2"
                className="outline-none border-2 border-secondary-500 rounded-lg text-black pencialicon"
              />
            </div>
          </div>

          {/* Row 6: State, City */}
          <div className="grid grid-cols-3 gap-5">
            <div className="flex flex-col w-72">
              <label htmlFor="state" className="text-text-hint mb-1">
                State
              </label>
              <select
                {...register('state')}
                id="state"
                className="outline-none border-2 border-secondary-500 rounded-lg text-black dropdown"
              >
                <option value="">-- Select State --</option>
                {mapToOption(referenceData.states?.state || [], 'name').map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col w-72">
              <label htmlFor="city" className="text-text-hint mb-1">
                City
              </label>
              <input
                {...register('city')}
                type="text"
                placeholder=""
                id="city"
                className="outline-none border-2 border-secondary-500 rounded-lg text-black pencialicon"
              />
            </div>
          </div>

          {/* Row 7: Zip Code, Country */}
          <div className="grid grid-cols-3 gap-5">
            <div className="flex flex-col w-72">
              <label htmlFor="zipCode" className="text-text-hint mb-1">
                Zip Code<span className="text-danger-500">*</span>
              </label>
              <input
                {...register('zipCode', {
                  required: 'Zip Code is required',
                  validate: (value) => value?.trim() !== '' || 'Zip Code cannot be blank',
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: 'Please enter a valid Zip Code (e.g., 12345 or 12345-6789)',
                  },
                })}
                type="text"
                placeholder="12345"
                id="zipCode"
                maxLength={10}
                className={`outline-none border-2 rounded-lg text-black pencialicon ${errors.zipCode ? 'border-danger-500' : 'border-secondary-500'}`}
              />
              {errors.zipCode && (
                <span className="text-danger-500 text-sm">{errors.zipCode.message}</span>
              )}
            </div>
            <div className="flex flex-col w-72">
              <label htmlFor="country" className="text-text-hint mb-1">
                Country
              </label>
              <input
                {...register('country')}
                type="text"
                placeholder=""
                id="country"
                className="outline-none border-2 border-secondary-500 rounded-lg text-black pencialicon"
              />
            </div>
          </div>

          {/* Row 8: Notes */}
          <div className="grid grid-cols-3 gap-5">
            <div className="flex flex-col col-span-3">
              <label htmlFor="notes" className="text-text-hint mb-1">
                Notes
              </label>
              <textarea
                {...register('notes')}
                placeholder="Notes"
                id="notes"
                className="focus:outline-none border rounded-lg text-black"
                rows={4}
              />
            </div>
          </div>

          {/* Row 11: Vendor Status */}
          <div className="grid grid-cols-3 gap-6">
            <div className="">
              <label>
                Vendor Status<span className="text-red-500">*</span>
              </label>
              <ul className="grid grid-cols-3 mt-1">
                <li className="relative">
                  <input
                    {...register('vendorStatus', {
                      required: 'Vendor status is required',
                    })}
                    type="radio"
                    value="Active"
                    id="active"
                    className="sr-only peer"
                  />
                  <label
                    className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 rounded-tl-full rounded-bl-full cursor-pointer focus:outline-none hover:bg-gray-50 ${
                      vendorStatus === 'Active'
                        ? '!bg-secondary-500 font-bold avtivebold text-white border-secondary-800'
                        : ''
                    }`}
                    htmlFor="active"
                  >
                    Active
                  </label>
                </li>
                <li className="relative">
                  <input
                    {...register('vendorStatus', {
                      required: 'Vendor status is required',
                    })}
                    type="radio"
                    value="Inactive"
                    id="in_active"
                    className="sr-only peer"
                  />
                  <label
                    className={`flex justify-center items-center py-2 px-3 bg-white border border-t-secondary-800 border-b-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 ${
                      vendorStatus === 'Inactive'
                        ? '!bg-secondary-500 text-white border-secondary-800 font-bold'
                        : ''
                    }`}
                    htmlFor="in_active"
                  >
                    Inactive
                  </label>
                </li>
                <li className="relative">
                  <input
                    {...register('vendorStatus', {
                      required: 'Vendor status is required',
                    })}
                    type="radio"
                    value="Fraud"
                    id="fraud"
                    className="sr-only peer"
                  />
                  <label
                    className={`flex justify-center items-center py-2 px-3 bg-white text-danger-500 border border-secondary-800 rounded-tr-full rounded-br-full cursor-pointer focus:outline-none hover:bg-gray-50 ${
                      vendorStatus === 'Fraud'
                        ? '!bg-secondary-500 font-bold text-white border-secondary-800'
                        : ''
                    }`}
                    htmlFor="fraud"
                  >
                    Fraud
                  </label>
                </li>
              </ul>
              {errors.vendorStatus && (
                <span className="text-danger-500">{errors.vendorStatus.message}</span>
              )}
            </div>
          </div>
        </form>

        {/* Audit Fields - Only show in Edit Mode */}
        {isEditMode && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Created By</label>
              <p className="text-sm text-gray-600">{defaultValues?.created_by || 'N/A'}</p>
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Date Created</label>
              <p className="text-sm text-gray-600">
                {defaultValues?.created ? formatDateCST(defaultValues.created) : 'N/A'}
              </p>
              {defaultValues?.created && (
                <p className="text-xs text-gray-500">{formatTimeCST(defaultValues.created)}</p>
              )}
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Updated By</label>
              <p className="text-sm text-gray-600">{defaultValues?.updated_by || 'N/A'}</p>
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Last Updated</label>
              <p className="text-sm text-gray-600">
                {defaultValues?.updated ? formatDateCST(defaultValues.updated) : 'N/A'}
              </p>
              {defaultValues?.updated && (
                <p className="text-xs text-gray-500">{formatTimeCST(defaultValues.updated)}</p>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between p-6 mt-6">
          <button
            onClick={handleClose}
            className="border border-secondary-800 text-secondary-800 py-2 px-5 rounded-full"
          >
            Close
          </button>
          <button
            onClick={handleSubmit(handleFormSubmit)}
            className={`bg-secondary-700 text-text-light py-2 px-5 rounded-full transition duration-200 ${
              isEditMode ? (isEdited ? 'opacity-100 cursor-pointer' : 'opacity-50 cursor-not-allowed') : ''
            }`}
            disabled={isEditMode ? !isEdited : false}
          >
            {isEditMode ? 'Update Details' : 'Add Contact'}
          </button>
        </div>
      </div>
    </Modal>

    <AddressValidationDialog
      dialogState={dialogState}
      onAcceptSuggestion={acceptSuggestion}
      onKeepOriginal={keepOriginal}
      onSaveAnyway={saveAnyway}
      onCancel={cancelDialog}
    />
    </>
  );
};

export default SalesContactsModal;
